/**
 * Global Audit service — MOCK-FIRST (CT-3901).
 *
 * Studio-wide immutable configuration audit trail. Destination of every
 * "View full audit log →". Shared severity vocabulary (auditConstants.js).
 * Platform envelope, snake_case.
 *
 * TODO(CT-3904 / backend): replace with `POST /v1/workspace/audit/list`.
 */

const MIN = 60_000;
const HR = 60 * MIN;
const DAY = 24 * HR;

const RANGE_MS = { '7d': 7 * DAY, '30d': 30 * DAY, '90d': 90 * DAY };

const startOfLocalDay = (now = Date.now()) => {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const matchesAuditSearch = (e, q) => {
  if (!q) return true;
  const haystack = [e.event, e.actor?.name, e.provider_slug, e.instance_name, e.studio]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
};

/** Stable synthetic actor id for the mock — `sys` for system, else a name slug. */
const actorId = (name, kind) =>
  kind === 'system'
    ? 'sys'
    : `${kind === 'agent' ? 'agt' : 'usr'}_${name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '')}`;

function ev(id, timestamp, severity, code, providerSlug, actorName, actorKind, studio, ip, change = {}) {
  // `change` carries the event payload: what triggered it (`details`), the
  // before/after state (CT-4212), and `instanceNum` — which of the provider's
  // instances this event belongs to (default 1), so a `?instance=<slug>-inst-N`
  // deep link (CT-4213) lands on the right instance's trail.
  const { details = null, before = null, after = null, instanceNum = 1 } = change;
  // Distribution changes are provider/studio-level (no instance); everything
  // else is instance-scoped, so a `?instance=` deep link (CT-4192) can pre-filter.
  const instanceScoped = !code.startsWith('distribution.');
  return {
    id,
    timestamp,
    severity,
    code,
    event: code,
    provider_slug: providerSlug,
    provider_name: providerSlug,
    resource: providerSlug,
    instance_id: instanceScoped ? `${providerSlug}-inst-${instanceNum}` : null,
    instance_name: instanceScoped ? `${providerSlug} — ${instanceNum === 1 ? 'Production' : 'Staging'}` : null,
    actor: { id: actorId(actorName, actorKind), name: actorName, kind: actorKind, ip },
    studio,
    ip,
    details,
    // Before/after state of the change, shown in the entry detail + CSV export (CT-4212).
    before,
    after,
  };
}

const buildSeed = () => {
  const at = (ago) => new Date(Date.now() - ago).toISOString();
  return [
    ev('e1', at(2 * MIN), 'crit', 'integration.health_failed', 'zendesk', 'System', 'system', 'admin', null, { details: { reason: 'Auth token expired' }, before: { status: 'healthy' }, after: { status: 'failed', reason: 'Auth token expired' } }),
    ev('e2', at(18 * MIN), 'warn', 'instance.credentials_rotated', 'salesforce', 'Danilo Petrov', 'user', 'admin', '10.0.0.4', { details: { secret: '***' }, before: { secret_version: 'v4' }, after: { secret_version: 'v5' } }),
    ev('e3', at(HR), 'info', 'instance.capability_toggled', 'salesforce', 'Maria Gomez', 'user', 'agentic', '10.0.0.4', { details: { read_records: true }, before: { read_records: false }, after: { read_records: true } }),
    ev('e4', at(4 * HR), 'info', 'distribution.studio_enabled', 'github', 'Danilo Petrov', 'user', 'admin', '10.0.0.4', { details: { agentic: true }, before: { agentic: false }, after: { agentic: true } }),
    ev('e5', at(12 * HR), 'warn', 'integration.schema_drift', 'notion', 'System', 'system', 'agentic', null, { details: {}, before: { schema_version: 'v2' }, after: { schema_version: 'v3' } }),
    ev('e6', at(26 * HR), 'info', 'access.granted', 'slack', 'Danilo Petrov', 'user', 'admin', '10.0.0.4', { details: { entity: 'Support Triage Agent' }, before: { access: 'none' }, after: { access: 'granted', entity: 'Support Triage Agent' } }),
    ev('e7', at(2 * DAY), 'info', 'instance.created', 'snowflake', 'Maria Gomez', 'user', 'admin', '10.0.0.4', { details: {}, before: {}, after: { status: 'active' } }),
    ev('e8', at(3 * DAY), 'info', 'integration.used_by_agent', 'slack', 'Support Triage Bot', 'agent', 'agentic', '10.0.4.12', { before: { calls_today: 11 }, after: { calls_today: 12 } }),
    ev('e9', at(4 * DAY), 'warn', 'distribution.surface_hidden', 'amazon-s3', 'Elena Soto', 'user', 'admin', '192.168.1.10', { details: { surface: 'data_studio' }, before: { data_studio: 'visible' }, after: { data_studio: 'hidden' } }),
    ev('e10', at(5 * DAY), 'info', 'integration.permissions_changed', 'jira', 'María González', 'user', null, '192.168.1.42', { details: { added_scopes: ['read'] }, before: { scopes: [] }, after: { scopes: ['read'] } }),
    ev('e11', at(6 * DAY), 'info', 'instance.created', 'hubspot', 'Danilo Petrov', 'user', 'admin', '10.0.0.4', { details: {}, before: {}, after: { status: 'active' } }),
    ev('e12', at(8 * DAY), 'crit', 'integration.health_failed', 'zendesk', 'System', 'system', 'admin', null, { details: { reason: 'Auth token revoked' }, before: { status: 'healthy' }, after: { status: 'failed', reason: 'Auth token revoked' } }),
    ev('e13', at(10 * DAY), 'info', 'distribution.surface_exposed', 'linear', 'Elena Soto', 'user', 'admin', '192.168.1.10', { details: { surface: 'agent_studio' }, before: { agent_studio: 'hidden' }, after: { agent_studio: 'exposed' } }),
    ev('e14', at(14 * DAY), 'warn', 'integration.health_degraded', 'github', 'System', 'system', null, null, { details: { reason: 'Rate limited' }, before: { status: 'healthy' }, after: { status: 'degraded', reason: 'Rate limited' } }),
    ev('e15', at(18 * DAY), 'info', 'access.granted', 'salesforce', 'María González', 'user', 'admin', '192.168.1.42', { details: { entity: 'Sales Pod' }, before: { access: 'none' }, after: { access: 'granted', entity: 'Sales Pod' } }),
    ev('e16', at(22 * DAY), 'info', 'instance.capability_toggled', 'snowflake', 'Maria Gomez', 'user', 'governance', '10.0.0.4', { details: {}, before: { write_records: false }, after: { write_records: true } }),
    // Second-instance coverage (CT-4213): Salesforce has two instances, so the
    // `?instance=salesforce-inst-2` deep link resolves to a real (non-empty) trail.
    ev('e17', at(24 * DAY), 'info', 'instance.capability_toggled', 'salesforce', 'Maria Gomez', 'user', 'agentic', '10.0.0.4', { details: { write_records: true }, before: { write_records: false }, after: { write_records: true }, instanceNum: 2 }),
  ];
};

const envelope = (body, meta = {}) => ({ status_code: 200, body, meta });

/**
 * List audit events, filtered (CT-4210/4211). Newest first.
 * @param {{ provider?, instance?, severities?, actor?, code?, search?, date_range?, page?, page_size? }} params
 */
export const listAuditEvents = (params = {}) => {
  const {
    provider,
    instance,
    severities,
    actor,
    code,
    search,
    date_range,
    page = 1,
    page_size = 15,
  } = params;
  const q = (search ?? '').trim().toLowerCase();
  const cutoff =
    date_range === 'today'
      ? startOfLocalDay()
      : date_range && date_range !== 'all' && RANGE_MS[date_range]
        ? Date.now() - RANGE_MS[date_range]
        : null;

  const filtered = buildSeed().filter((e) => {
    if (provider && e.provider_slug !== provider) return false;
    if (instance && e.instance_id !== instance) return false;
    if (severities?.length && !severities.includes(e.severity)) return false;
    if (actor && e.actor.name !== actor) return false;
    if (code && e.code !== code) return false;
    if (cutoff != null && new Date(e.timestamp).getTime() < cutoff) return false;
    if (!matchesAuditSearch(e, q)) return false;
    return true;
  });

  const total = filtered.length;
  const start = (page - 1) * page_size;
  const items = filtered.slice(start, start + page_size);
  return Promise.resolve(
    envelope(items, { total, page, page_size, total_pages: Math.max(1, Math.ceil(total / page_size)) }),
  );
};

/**
 * All audit rows matching the filters, unpaginated — the source for CSV export
 * (CT-4212). Reuses `listAuditEvents`' filter logic so export always respects the
 * active filters + date range, not just the rows on the current page.
 * @param {object} params — same filter shape as `listAuditEvents` (page/page_size ignored).
 */
export const getAuditExport = (params = {}) =>
  listAuditEvents({ ...params, page: 1, page_size: Number.MAX_SAFE_INTEGER });

/** Distinct facets for the audit filters (CT-4211). */
export const getAuditFacets = () => {
  const seed = buildSeed();
  return Promise.resolve(
    envelope({
      providers: [...new Map(seed.map((e) => [e.provider_slug, e.provider_name])).entries()].map(
        ([slug, name]) => ({ slug, name }),
      ),
      instances: [
        ...new Map(
          seed
            .filter((e) => e.instance_id)
            .map((e) => [
              e.instance_id,
              { id: e.instance_id, name: e.instance_name, provider_slug: e.provider_slug },
            ]),
        ).values(),
      ],
      actors: [...new Set(seed.map((e) => e.actor.name))],
      codes: [...new Set(seed.map((e) => e.code))],
    }),
  );
};
